import {
  Box3,
  Group,
  MathUtils,
  Matrix4,
  Mesh,
  PerspectiveCamera,
  Vector2,
  Vector3,
  type BufferGeometry,
  type Material,
} from 'three'
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader'

import type {MonaColorVariant} from '../config'
import type {
  MonaCanvasFaceDirection,
  MonaCanvasFraming,
  MonaCanvasMouseInteractionType,
  MonaCanvasOpeningAnimation,
} from '../types'
import MonaGLB from './mona-smile.glb'
import MainMona from './main-mona'
import PointerTrail from './pointer-trail'
import {handleFacePointer, updateFaceInteraction} from './face-interaction'
import {handleFollowPointer, updateFollowInteraction, updateFollowTargets} from './follow-interaction'
import {updateOpeningGaze} from './opening-gaze'

export default class FirstScene {
  camera = new PerspectiveCamera(10, 1, 0.1, 100)
  group = new Group()
  fullViewportCamera = new PerspectiveCamera(10, 1, 0.1, 100)
  monaGroup = new Group()
  mainMona: MainMona
  mousePos = new Vector2()
  mousePosTarget = new Vector2()
  facingPos = new Vector2()
  facingPosTarget = new Vector2()
  facingRotationPos = new Vector2()
  facingRotationPosTarget = new Vector2()
  nextPointerPosition = new Vector2()
  isAwaitingOpeningPointer = false
  isBlendingOpeningPointer = false
  openingHandoffPointer = new Vector2()
  facingPointerPosition = new Vector2()
  openingAnimation: MonaCanvasOpeningAnimation | undefined
  pointerTrail = new PointerTrail()
  facePosition = new Vector3()
  faceAnchorPosition = new Vector3()
  followHeadBounds = new Box3()
  followHeadBoundsCorners: Vector3[] = []
  followHeadBoundsMatrix = new Matrix4()
  followHeadBoundsPoint = new Vector3()
  pointerRay = new Vector3()
  pointerWorldPosition = new Vector3()
  monaVisualCenter = new Vector3()
  monaVisualWorldSize = new Vector2()
  projectedMonaCenter = new Vector3()
  forwardRotationY: number
  mouseMovementAmount = 0
  horizontalTiltScale: number
  facingRotationScale: number
  facingPitchOffset: number
  hasMonaVisualBounds = false
  modelScale = 1
  hasStartedOpening = false
  isResting = false
  isOpening: boolean
  isMouseInteractionBlocked: boolean
  disposed = false
  onError: (error: unknown) => void
  onOpeningStart?: () => void
  onOpeningComplete?: () => void
  readonly framing: MonaCanvasFraming
  mouseInteractionType: MonaCanvasMouseInteractionType = 'follow'
  mouseInteractionRadius: number | undefined
  faceRestingPitch = 0
  facePitchOffset = 0
  hasFacePointer = false
  faceDirection: MonaCanvasFaceDirection | null = null
  openingLookAt = new Group()
  readonly reduceMotion: boolean
  readonly createOpeningAnimation?: () => MonaCanvasOpeningAnimation

  constructor(
    colorVariant: MonaColorVariant,
    onError: (error: unknown) => void,
    reduceMotion: boolean,
    createOpeningAnimation?: () => MonaCanvasOpeningAnimation,
    onOpeningStart?: () => void,
    onOpeningComplete?: () => void,
    framing: MonaCanvasFraming = 'scene',
  ) {
    this.framing = framing
    this.onError = onError
    this.onOpeningStart = onOpeningStart
    this.onOpeningComplete = onOpeningComplete
    this.reduceMotion = reduceMotion
    this.createOpeningAnimation = createOpeningAnimation
    this.openingAnimation = reduceMotion ? undefined : createOpeningAnimation?.()
    this.isOpening = this.openingAnimation !== undefined
    this.isMouseInteractionBlocked = this.isOpening
    this.horizontalTiltScale = this.isOpening ? 0 : 1
    this.facingRotationScale = this.openingAnimation?.facingRotationScale ?? 1
    this.facingPitchOffset = this.openingAnimation?.facingPitchOffset ?? 0
    this.mainMona = new MainMona(colorVariant)
    this.camera.position.z = 2
    this.camera.lookAt(0, 0, 0)
    this.camera.updateProjectionMatrix()
    this.camera.updateMatrixWorld()
    this.fullViewportCamera.copy(this.camera)
    this.fullViewportCamera.updateMatrixWorld()

    this.forwardRotationY = Math.atan2(
      this.camera.position.x - this.monaGroup.position.x,
      this.camera.position.z - this.monaGroup.position.z,
    )
    this.monaGroup.scale.setScalar(0.015)
    this.monaGroup.rotation.y = this.forwardRotationY
    this.monaGroup.add(this.mainMona.group)
    this.group.add(this.monaGroup)

    void this.createMona()
  }

  show() {
    this.group.visible = true
  }

  hide() {
    this.group.visible = false
    this.pointerTrail.clear()
  }

  handleMouseMove(event: MouseEvent, rect: DOMRect) {
    if (!this.group.visible || this.isMouseInteractionBlocked || this.mouseInteractionType === 'none') return
    if (rect.width <= 0 || rect.height <= 0) return

    if (this.mouseInteractionType === 'face') {
      this.hasFacePointer = handleFacePointer(this.facingPosTarget, event, rect, this.mouseInteractionRadius)
      return
    }

    handleFollowPointer(this, event, rect)
  }

  setMouseInteraction(type: MonaCanvasMouseInteractionType, radius?: number, restingPitch = 0) {
    if (!Number.isFinite(restingPitch)) {
      this.onError(new RangeError('Mona resting pitch must be finite'))
      return
    }
    if (
      type !== this.mouseInteractionType &&
      (type === 'face' || this.mouseInteractionType === 'face') &&
      !this.isOpening &&
      !this.faceDirection
    ) {
      this.resetPose()
    }
    this.mouseInteractionType = type
    this.mouseInteractionRadius = radius
    this.faceRestingPitch = restingPitch
  }

  resetMouseFacing() {
    if (this.mouseInteractionType === 'face') {
      this.facingPosTarget.set(0, 0)
      this.hasFacePointer = false
    }
  }

  setFaceDirection(direction?: MonaCanvasFaceDirection | null) {
    this.faceDirection = direction ?? null
  }

  resize(width: number, height: number) {
    if (width <= 0 || height <= 0) return

    const referenceViewportAspect = 13 / 8
    const aspect = width / height
    const zoom = aspect / referenceViewportAspect
    this.camera.clearViewOffset()
    this.camera.aspect = aspect
    this.camera.zoom = zoom
    this.camera.updateProjectionMatrix()
    this.fullViewportCamera.aspect = aspect
    this.fullViewportCamera.zoom = zoom
    this.fullViewportCamera.updateProjectionMatrix()
    this.fitMona()
  }

  fitMona() {
    if (this.framing !== 'contain' || !this.hasMonaVisualBounds) return

    // Fit the loaded artwork, not its canvas, with a little room around the silhouette.
    const visibleHeight = 2 * Math.tan(MathUtils.degToRad(this.camera.fov * 0.5)) * this.camera.position.z
    const zoom =
      (visibleHeight * 0.7) / Math.max(this.monaVisualWorldSize.y, this.monaVisualWorldSize.x / this.camera.aspect)
    this.camera.zoom = zoom
    this.fullViewportCamera.zoom = zoom
    this.camera.updateProjectionMatrix()
    this.fullViewportCamera.updateProjectionMatrix()
  }

  setResolution(width: number, height: number) {
    this.mainMona.setResolution(width, height)
  }

  setModelScale(scale: number) {
    if (!Number.isFinite(scale) || scale <= 0) throw new RangeError('Mona model scale must be a positive finite number')
    if (scale === this.modelScale) return

    const ratio = scale / this.modelScale
    this.monaGroup.scale.multiplyScalar(ratio)
    this.monaVisualWorldSize.multiplyScalar(ratio)
    this.modelScale = scale
    this.fitMona()
  }

  setRenderViewport(
    fullWidth: number,
    fullHeight: number,
    offsetX: number,
    offsetY: number,
    width: number,
    height: number,
  ) {
    const view = this.camera.view
    if (
      view?.enabled &&
      view.fullWidth === fullWidth &&
      view.fullHeight === fullHeight &&
      view.offsetX === offsetX &&
      view.offsetY === offsetY &&
      view.width === width &&
      view.height === height
    ) {
      return
    }
    this.camera.setViewOffset(fullWidth, fullHeight, offsetX, offsetY, width, height)
  }

  getMonaViewportMetrics(viewportHeight: number, center: Vector2, size: Vector2) {
    if (!this.hasMonaVisualBounds) return false

    this.projectedMonaCenter.copy(this.monaVisualCenter)
    this.monaGroup.localToWorld(this.projectedMonaCenter)
    const visibleWorldHeight = this.getVisibleWorldHeight(this.projectedMonaCenter.z)
    const pixelsPerWorldUnit = viewportHeight / visibleWorldHeight

    this.projectedMonaCenter.project(this.fullViewportCamera)
    center.set(this.projectedMonaCenter.x * 0.5 + 0.5, 0.5 - this.projectedMonaCenter.y * 0.5)
    size.copy(this.monaVisualWorldSize).multiplyScalar(pixelsPerWorldUnit)
    return true
  }

  getVisibleWorldHeight(worldZ: number) {
    const distanceToCamera = Math.abs(this.fullViewportCamera.position.z - worldZ)
    return (
      (2 * Math.tan(MathUtils.degToRad(this.fullViewportCamera.fov * 0.5)) * distanceToCamera) /
      this.fullViewportCamera.zoom
    )
  }

  getFollowTopLimit() {
    if (!this.hasMonaVisualBounds || this.followHeadBoundsCorners.length === 0) return Infinity

    const head = this.mainMona.monaHeadGroup
    head.updateWorldMatrix(true, false)
    if (this.followHeadBounds.isEmpty() || !this.followHeadBoundsMatrix.equals(head.matrixWorld)) {
      this.followHeadBoundsMatrix.copy(head.matrixWorld)
      this.followHeadBounds.makeEmpty()
      for (const corner of this.followHeadBoundsCorners) {
        this.followHeadBoundsPoint.copy(corner).applyMatrix4(head.matrixWorld)
        this.followHeadBounds.expandByPoint(this.followHeadBoundsPoint)
      }
    }

    const topOverflowFraction = 0.05
    const visibleHeight = this.getVisibleWorldHeight(this.followHeadBounds.max.z)
    const headTopOffset = this.followHeadBounds.max.y - this.monaGroup.position.y
    return this.fullViewportCamera.position.y + visibleHeight * (0.5 + topOverflowFraction) - headTopOffset
  }

  update(delta: number) {
    if (!this.group.visible) return

    if (!this.isOpening && this.faceDirection) {
      this.facingPos.set(0, 0)
      this.facePitchOffset = this.faceRestingPitch
      updateOpeningGaze(this.mainMona, this.faceDirection, this.openingLookAt, delta)
      return
    }

    if (!this.isOpening && this.mouseInteractionType === 'face') {
      this.facePitchOffset = updateFaceInteraction(
        this.mainMona,
        this.facingPos,
        this.facingPosTarget,
        delta,
        this.facePitchOffset,
        this.hasFacePointer ? 0 : this.faceRestingPitch,
      )
      return
    }

    if (this.isResting) {
      this.mainMona.updateRestingPose(delta, this.openingAnimation?.restingYawRadians ?? 0)
      return
    }

    updateFollowInteraction(this, delta)
  }

  resetPose(pointerPosition?: Vector2) {
    this.isResting = true
    this.isAwaitingOpeningPointer = false
    this.isBlendingOpeningPointer = false
    if (pointerPosition) {
      this.nextPointerPosition.copy(pointerPosition)
    } else {
      this.nextPointerPosition.set(0, 0)
    }
    this.pointerTrail.clear()
    this.pointerTrail.sample(this.nextPointerPosition)
    this.updatePointerWorldPosition(this.nextPointerPosition)
    this.monaGroup.position.copy(this.pointerWorldPosition)
    this.mousePos.set(0, 0)
    this.mousePosTarget.set(0, 0)
    this.facingPos.set(0, 0)
    this.facingPosTarget.set(0, 0)
    this.facePitchOffset = 0
    this.hasFacePointer = false
    this.facingRotationPos.set(0, 0)
    this.facingRotationPosTarget.set(0, 0)
    this.facingPointerPosition.set(0, 0)
    this.mouseMovementAmount = 0
    this.horizontalTiltScale = 1
    this.facingRotationScale = 1
    this.facingPitchOffset = 0
  }

  restartOpening() {
    if (this.reduceMotion || !this.createOpeningAnimation) return

    const animation = this.createOpeningAnimation()
    this.openingAnimation?.dispose?.()
    this.resetPose()
    this.pointerTrail.clear()
    this.mainMona.commonUniforms.uTime.value = 0
    this.mainMona.tentaclesGroup.rotation.x = 0
    this.mainMona.updateRestingPose(0)
    this.faceDirection = null
    this.openingAnimation = animation
    this.hasStartedOpening = false
    this.isOpening = true
    this.isResting = false
    this.isMouseInteractionBlocked = true
    this.horizontalTiltScale = 0
    this.facingRotationScale = animation.facingRotationScale ?? 1
    this.facingPitchOffset = animation.facingPitchOffset ?? 0
  }

  skipOpening() {
    if (!this.isOpening) return

    this.openingAnimation?.dispose?.()
    this.isOpening = false
    this.isMouseInteractionBlocked = false
    this.resetPose()
    this.mainMona.tentaclesGroup.rotation.x = 0
    this.mainMona.updateRestingPose(0)
    this.onOpeningComplete?.()
  }

  dispose() {
    this.disposed = true
    this.openingAnimation?.dispose?.()
    const geometries = new Set<BufferGeometry>()
    const materials = new Set<Material>()
    this.group.traverse(child => {
      if (!(child instanceof Mesh)) return
      geometries.add(child.geometry)
      if (Array.isArray(child.material)) {
        for (const material of child.material) materials.add(material)
      } else {
        materials.add(child.material)
      }
    })
    for (const geometry of geometries) geometry.dispose()
    for (const material of materials) material.dispose()
  }

  async createMona() {
    try {
      const gltf = await new GLTFLoader().loadAsync(MonaGLB)
      if (this.disposed) return

      this.mainMona.create(gltf.scene)
      this.measureMona()
    } catch (error) {
      if (!this.disposed) this.onError(error)
    }
  }

  updateMouseTargets(
    movementPointerPosition = this.pointerTrail.hasSample
      ? this.pointerTrail.position
      : this.pointerTrail.pointerPosition,
    targetPointerPosition = this.pointerTrail.pointerPosition,
    delta = 0,
  ) {
    if (this.isBlendingOpeningPointer) {
      const openingHandoffEasingSpeed = 3
      this.openingHandoffPointer.lerp(this.nextPointerPosition, 1 - Math.exp(-delta * openingHandoffEasingSpeed))
      updateFollowTargets(this, this.openingHandoffPointer, this.openingHandoffPointer)
      if (this.openingHandoffPointer.distanceTo(this.nextPointerPosition) < 0.001) {
        this.isBlendingOpeningPointer = false
        this.pointerTrail.clear()
        this.pointerTrail.sample(this.nextPointerPosition)
      }
      return
    }
    if (this.isOpening && this.openingAnimation) {
      const pointerPosition = this.openingAnimation.pointerPosition
      updateFollowTargets(this, pointerPosition, pointerPosition)
      return
    }
    updateFollowTargets(this, movementPointerPosition, targetPointerPosition)
  }

  updatePointerWorldPosition(pointerPosition: Vector2) {
    this.pointerRay
      .set(pointerPosition.x, pointerPosition.y, 0.5)
      .unproject(this.fullViewportCamera)
      .sub(this.fullViewportCamera.position)
      .normalize()

    const distanceToModel = (this.monaGroup.position.z - this.fullViewportCamera.position.z) / this.pointerRay.z
    this.pointerWorldPosition.copy(this.fullViewportCamera.position).addScaledVector(this.pointerRay, distanceToModel)
    this.pointerWorldPosition.z = this.monaGroup.position.z
  }

  updateOpening(delta: number) {
    const animation = this.openingAnimation
    if (!animation || animation.hasReachedEnd) return

    if (!this.hasStartedOpening) {
      this.updateMouseTargets()
      this.monaGroup.position.copy(this.pointerWorldPosition)
      if (animation.initialFacingDirection) {
        this.facingRotationPos.set(animation.initialFacingDirection === 'right' ? 0.5 : -0.5, 0)
      }
      this.hasStartedOpening = true
      this.onOpeningStart?.()
      return
    }
    animation.update(delta)
  }

  measureMona() {
    const rotation = this.mainMona.group.quaternion.clone()
    // Loading can finish during the opening gaze; contain framing must use the neutral silhouette.
    if (this.framing === 'contain') this.mainMona.group.quaternion.identity()
    this.group.updateWorldMatrix(true, true)
    const bounds = new Box3().setFromObject(this.monaGroup)
    const worldSize = bounds.getSize(new Vector3())

    bounds.getCenter(this.monaVisualCenter)
    this.monaGroup.worldToLocal(this.monaVisualCenter)
    if (this.framing === 'contain') {
      // Center the model itself so the existing pointer/camera coordinate system stays unchanged.
      this.mainMona.group.position.sub(this.monaVisualCenter)
      this.monaVisualCenter.set(0, 0, 0)
    }
    this.mainMona.group.quaternion.copy(rotation)
    this.group.updateWorldMatrix(true, true)
    this.cacheFollowHeadBounds()
    this.monaVisualWorldSize.set(worldSize.x, worldSize.y)
    this.hasMonaVisualBounds = this.monaVisualWorldSize.x > 0 && this.monaVisualWorldSize.y > 0
    this.fitMona()
  }

  cacheFollowHeadBounds() {
    const head = this.mainMona.monaHeadGroup
    const inverseHeadMatrix = head.matrixWorld.clone().invert()
    const meshToHeadMatrix = new Matrix4()
    this.followHeadBoundsCorners.length = 0
    this.followHeadBounds.makeEmpty()
    head.traverse(child => {
      if (!(child instanceof Mesh)) return

      if (!child.geometry.boundingBox) child.geometry.computeBoundingBox()
      const bounds = child.geometry.boundingBox
      if (!bounds) throw new Error('Unable to measure Mona head geometry')
      if (bounds.isEmpty()) return

      meshToHeadMatrix.multiplyMatrices(inverseHeadMatrix, child.matrixWorld)
      // Preserve each mesh's box corners; merging them first would inflate the bounds when Mona turns.
      for (let corner = 0; corner < 8; corner++) {
        this.followHeadBoundsCorners.push(
          new Vector3(
            corner & 1 ? bounds.max.x : bounds.min.x,
            corner & 2 ? bounds.max.y : bounds.min.y,
            corner & 4 ? bounds.max.z : bounds.min.z,
          ).applyMatrix4(meshToHeadMatrix),
        )
      }
    })
  }
}
